<template>
  <div
    class="w-full rounded-3xl mb-5 border-2 bg-white !border-[#2191da] p-5 lg:p-7 lg:pb-10 gap-14 grid content-start max-w-[500px]">
    <div class="w-full flex justify-center items-center">
      <h2 class="font-black text-[#3057B4] text-3xl md:text-2xl font-openSans">Create your WriteWise Account</h2>
    </div>
    <form class="w-full px-3 grid gap-7 content-start">
      <v-alert style="width: 100%;" :model-value="!!message" :type="status == 'success' ? 'success' : 'error'">{{
        message
      }}</v-alert>
      <GoogleAuth @token="handleGoogleSignup" />
      <v-text-field v-model="formData.first_name" class="!border-2 !border-primary-dark px-2 py-1 rounded-md"
        :disabled="loadingResponse" v-on:keyup.enter="onEnterKey" name="first name" label="First Name" type="text"
        variant="plain" :hide-details="true" />
      <v-text-field v-model="formData.last_name" class="!border-2 !border-primary-dark px-2 py-1 rounded-md"
        :disabled="loadingResponse" v-on:keyup.enter="onEnterKey" name="last name" label="Last Name" type="text"
        variant="plain" :hide-details="true" />
      <v-text-field v-model="formData.email" class="!border-2 !border-primary-dark px-2 py-1 rounded-md"
        :disabled="loadingResponse" v-on:keyup.enter="onEnterKey" name="email" label="Email" type="email"
        variant="plain" :hide-details="true" />

      <v-text-field v-model="formData.password" class="!border-2 !border-primary-dark px-2 py-1 rounded-md"
        :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'" :type="showPassword ? 'text' : 'password'"
        :disabled="loadingResponse" v-on:keyup.enter="onEnterKey" name="input-10-1" label="Password" variant="plain"
        @click:append="showPassword = !showPassword" :hide-details="true" />


      <div class="w-full grid gap-2">
        <!-- <v-checkbox label="I accept the Terms of Use" class="!text-gray-600 !h-5 shrink mr-0 mt-0 !p-0" />  -->
        <div class="text-gray-600 flex items-center gap-x-3">
          <input id="terms-of-use" type="checkbox" v-model="terms" />
          <label for="terms-of-use">
            I accept the
            <a href="https://web.writewise.io/terms_of_use/" target="_blank" rel="noopener noreferrer"
              class="underline">Terms of Use</a>
          </label>
        </div>
        <v-btn
          :disabled="!formData.first_name || !formData.last_name || !formData.email || !formData.password || loadingResponse || !isEmailValid || !terms"
          variant="flat"
          class="!py-3 !bg-[#3057B4] !font-openSans !font-black !text-xl !capitalize !text-white !h-auto !tracking-normal"
          @click="signUp" :loading="loadingResponse">Create Account
        </v-btn>

        <div class="text-gray-600 text-left font-openSans text-sm">
          <span>Already have an account?</span>
          <a class="cursor-pointer underline" v-on:click="goLogin('english')">
            Login WriteWise English
          </a>
          <span> / </span>
          <a class="cursor-pointer underline" v-on:click="goLogin('spanish')">
            Login WriteWise Spanish
          </a>
        </div>
        <div class="text-gray-600 text-left font-openSans text-sm">
          <span>Have an account but not a plan? </span>
          <a class="cursor-pointer underline" href="https://web.writewise.io/writewise-trial">
            Purchase a plan here
          </a>
        </div>
      </div>
    </form>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { signUp, googleAuth } from '../../actions/auth';
import { usePayloadStore } from '../../stores/payload';
import Message from 'antd-message';
import GoogleAuth from '@/components/auth/GoogleAuth.vue';

@Options({
  components: {
    GoogleAuth
  }
})

export default class SignUpForm extends Vue {
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    this.utmSource = urlParams.get('utm_source') ?? '';
    this.utmMedium = urlParams.get('utm_medium') ?? '';
    this.utmCampaign = urlParams.get('utm_campaign') ?? '';
  }

  public utmSource: string = '';
  public utmMedium: string = '';
  public utmCampaign: string = '';

  public loadingResponse = false;
  public webBaseUrl = process.env.WEB_BASE_URL;
  public spanishWebBaseUrl = process.env.WEB_BASE_URL;
  public formData = {
    first_name: '',
    last_name: '',
    email: '',
    password: ''
  };

  get isEmailValid(): boolean {
    return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(this.formData.email);
  }
  public status = '';

  public showPassword = false;

  public terms = false;

  public message = '';

  public goLogin(version: string) {
    if (version === 'english') window.location.href = 'https://writewise.io/accounts/login';
    else window.location.href = 'https://es.writewise.io/accounts/login';
  }

  private payloadStore = usePayloadStore();

  public onEnterKey = () => {
    if (this.formData.first_name && this.formData.last_name && this.formData.email && this.formData.password && !this.loadingResponse) {
      this.signUp();
    }
  };

  public signUp = () => {
    this.loadingResponse = true;
    signUp({
      ...this.formData, utm: {
        source: this.utmSource,
        medium: this.utmMedium,
        campaign: this.utmCampaign,
      },
    })
      .then((res: any) => {
        this.status = 'success';
        this.loadingResponse = false;
        this.message = 'Account created successfully! We will redirect you now so you can start your free trial.';
        setTimeout(() => {
          window.location.href = 'https://web.writewise.io/writewise-trial';
        }, 8000);
      })
      .catch((err) => {
        console.log(err);
        const exists = err?.response?.data?.some((text: string | string[]) => text.includes("exist"));
        if (exists) {
          this.status = 'success';
          this.message = 'You already have an account! We will redirect you now so you can select a plan.';
          setTimeout(() => {
            window.location.href = 'https://web.writewise.io/writewise-trial';
          }, 8000);
        } else {
          this.status = 'error';
          this.message = err?.response?.data?.detail || 'Sign up failed, please try again';
        }
        this.loadingResponse = false;
      });
  };

  public handleGoogleSignup = (token: string) => {
    this.loadingResponse = true;
    googleAuth({ token, utm: {
        source: this.utmSource,
        medium: this.utmMedium,
        campaign: this.utmCampaign,
      }, })
      .then((res: any) => {
        this.status = 'success';
        this.loadingResponse = false;
        if (res.token) {
          this.payloadStore.change({ user: res.user, token: res.token, refresh: res.refresh });
          window.location.href = '/dashboard/documents';
        } else {
          this.message = 'Account created successfully! We will redirect you now so you can start your free trial.';
          setTimeout(() => {
            window.location.href = 'https://web.writewise.io/writewise-trial';
          }, 8000);
        }
      })
      .catch((err) => {
        console.log(err);
        const exists = err?.response?.data?.error?.toLowerCase();
        if (exists === `you don't have active membership`) {
          this.status = 'success';
          this.message = 'You already have an account! We will redirect you now so you can select a plan.';
          setTimeout(() => {
            window.location.href = 'https://web.writewise.io/writewise-trial';
          }, 8000);
        } else {
          this.status = 'error';
          this.message = err?.response?.data?.detail || 'Google auth failed, please try again';
        }
        this.loadingResponse = false;
      });
  };
}
</script>
